import { motion } from "framer-motion";
import { useEffect } from "react";
import heroVideo from "../assests/Audio.mp4"; // 🎬 Import your local video

export default function AudioVideoTagging() {
   
  return (
    <div className="bg-gray-50 text-gray-900 ">
      {/* Hero Section with Background Video */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        {/* Hero Text */}
        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight drop-shadow-lg font-orbitron"
          >
            Audio & Video Tagging Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200 font-raleway"
          >
            Unlock richer AI models with precise tagging of audio and video data —
            from sound classification to scene-level annotations.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight text-gray-900 font-orbitron">
          Why Audio & Video Tagging Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Audio and video tagging helps AI systems recognize, categorize, and
          understand real-world multimedia content. From speech detection to
          activity recognition, tagging adds the context that powers smarter
          AI-driven applications.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-100 via-white to-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight text-gray-900 font-orbitron">
          Our Tagging Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "🎤 Audio Tagging",
              desc: "Identify sounds, classify speech, music, noise, and emotions for audio-based AI models.",
            },
            {
              title: "🎬 Video Tagging",
              desc: "Frame-by-frame annotation, scene labeling, and activity recognition for smarter video AI.",
            },
            {
              title: "🔗 Hybrid Tagging",
              desc: "Synchronize audio and video tags for complete multimodal datasets.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition duration-300"
            >
              <h3 className="text-2xl font-semibold mb-3 text-indigo-700">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight text-gray-900 font-orbitron">
          How We Deliver Accurate Tagging
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Collect or receive raw audio/video",
            "Define tagging schema & guidelines",
            "Human + AI-assisted tagging",
            "Quality check & delivery",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-50 via-white to-purple-50">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight text-gray-900 font-orbitron">
          Applications of Audio & Video Tagging
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "🎶 Music & Media",
              desc: "Tagging genres, moods, and instruments for streaming apps.",
            },
            {
              title: "👀 Surveillance",
              desc: "Detect unusual activity and sound events in security systems.",
            },
            {
              title: "🚗 Autonomous Driving",
              desc: "Recognize sirens, traffic signals, and pedestrian activities.",
            },
            {
              title: "🛒 Retail AI",
              desc: "Customer emotion, voice sentiment, and in-store activity tracking.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition duration-300"
            >
              <h3 className="font-semibold mb-2 text-xl text-indigo-700">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight text-gray-900 font-orbitron">
          Quality & Compliance
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          We follow rigorous quality assurance with multiple reviews,
          domain-specific experts, and GDPR-compliant processes to ensure secure
          and high-quality tagged datasets.
        </p>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight font-orbitron">
          Ready to Supercharge Your AI with Tagged Data?
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6 font-raleway">
          Let’s partner to build smarter multimedia AI systems. Contact us today.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Get Started
        </motion.button>
      </section>
    </div>
  );
}
